'use client'

import { forwardRef, useImperativeHandle } from 'react'
import { useParams } from 'next/navigation'
import type {
  EditReservationCustomerTabHandle,
  ReservationWithModification,
} from '@/root/modules/shared/EditReservation/types'
import { useCustomerEditState } from '@/root/modules/shared/EditReservation/hooks/useCustomerEditState'
import { useCustomRouter, useGetTranslations } from '@/root/shared-hooks'
import { CustomerInfoIcon } from '@/root/ui/shared/icons/CustomerInfoIcon'
import { PhoneBlueIcon } from '@/root/ui/shared/icons/PhoneBlueIcon'
import { LockRedIcon } from '@/root/ui/shared/icons/LockRedIcon'
import { Button } from '@/root/ui/tuuleliinid'
import { testIds } from '@/root/shared-consts'

interface EditReservationCustomerTabProps {
  reservation: ReservationWithModification
  onDirtyChange?: (dirty: boolean) => void
  onSaved?: (updated: ReservationWithModification) => void
}

export const EditReservationCustomerTab = forwardRef<EditReservationCustomerTabHandle, EditReservationCustomerTabProps>(
  ({ reservation, onDirtyChange, onSaved }, ref) => {
    const params = useParams()
    const reservationToken = params?.reservationToken as string | undefined
    const { locale } = useCustomRouter()
    const { getTranslation } = useGetTranslations()

    const {
      register,
      errors,
      isDirty,
      isSaving,
      saveError,
      onSubmit,
      save,
      resetForm,
      customerName,
      customerPhone,
      customerEmail,
    } = useCustomerEditState({ reservation, reservationToken, locale, getTranslation, onDirtyChange, onSaved })

    useImperativeHandle(ref, () => ({ save, resetForm }), [save, resetForm])

    return (
      <div className="rounded-2xl p-6">
        <div className="mb-6 flex flex-col gap-3">
          <div className="flex items-center gap-2 text-lg">
            <CustomerInfoIcon width={14} height={15} color="base-normal-light" />
            <span data-testid={testIds.edit.customerSummaryName}>{customerName || '-'}</span>
          </div>
          <div className="flex items-center gap-2 text-lg">
            <PhoneBlueIcon width={15} height={15} />
            <span data-testid={testIds.edit.customerSummaryPhone}>{customerPhone || '-'}</span>
          </div>
          <div className="flex items-center gap-2 text-lg">
            <LockRedIcon width={12} height={15} />
            <span data-testid={testIds.edit.customerSummaryEmail}>{customerEmail || '-'}</span>
          </div>
        </div>

        <form className="flex flex-col gap-6" onSubmit={onSubmit}>
          {saveError ? <span className="text-error text-sm">{saveError}</span> : null}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-neutral-darker text-sm leading-none">
                {getTranslation('selectTicketsFirstname')}
              </label>
              <input
                data-testid={testIds.edit.customerFirstName}
                className="border-neutral-medium-lighter focus:border-primary-base rounded-lg border bg-white px-3 py-2 text-base focus:outline-none"
                {...register('firstName')}
              />
              {errors.firstName && (
                <span className="text-error text-xs">
                  {getTranslation('confirmationContactFormInvalidFormatError')}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-neutral-darker text-sm leading-none">
                {getTranslation('selectTicketsLastname')}
              </label>
              <input
                data-testid={testIds.edit.customerLastName}
                className="border-neutral-medium-lighter focus:border-primary-base rounded-lg border bg-white px-3 py-2 text-base focus:outline-none"
                {...register('lastName')}
              />
              {errors.lastName && (
                <span className="text-error text-xs">
                  {getTranslation('confirmationContactFormInvalidFormatError')}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-neutral-darker text-sm leading-none">
                {getTranslation('confirmationContactFormPhoneNumber')}
              </label>
              <div className="flex items-start gap-2">
                <input
                  data-testid={testIds.edit.customerPhoneCode}
                  className="border-neutral-medium-lighter focus:border-primary-base w-28 rounded-lg border bg-white px-3 py-2 text-base focus:outline-none"
                  {...register('intlCode')}
                />
                <input
                  data-testid={testIds.edit.customerPhoneNumber}
                  className="border-neutral-medium-lighter focus:border-primary-base w-full rounded-lg border bg-white px-3 py-2 text-base focus:outline-none"
                  {...register('phoneNumber')}
                />
              </div>
              {(errors.intlCode || errors.phoneNumber) && (
                <span className="text-error text-xs">
                  {getTranslation('confirmationContactFormInvalidFormatError')}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-neutral-darker text-sm leading-none">
                {getTranslation('confirmationContactFormEmail')}
              </label>
              <input
                data-testid={testIds.edit.customerEmail}
                className="border-neutral-medium-lighter focus:border-primary-base rounded-lg border bg-white px-3 py-2 text-base focus:outline-none"
                {...register('email')}
              />
              {errors.email && (
                <span className="text-error text-xs">
                  {getTranslation('confirmationContactFormInvalidFormatError')}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              data-testid={testIds.edit.customerReset}
              type="button"
              onClick={resetForm}
              disabled={!isDirty || isSaving}
              className="border-neutral-medium-light text-neutral-medium-light disabled:border-neutral-medium-lighter disabled:text-neutral-medium-lighter not-disabled:hover:bg-base-medium-light flex h-9 flex-1 items-center justify-center rounded-lg border bg-transparent px-4 transition-colors not-disabled:cursor-pointer not-disabled:hover:border-transparent not-disabled:hover:text-white disabled:cursor-default"
            >
              {getTranslation('customerInformationCancel')}
            </button>
            <Button
              data-testid={testIds.edit.customerSave}
              variant="gray"
              type="submit"
              disabled={!isDirty || isSaving}
              className="flex h-9 flex-1 items-center justify-center rounded-lg"
            >
              {getTranslation('customerInformationSave')}
            </Button>
          </div>
        </form>
      </div>
    )
  }
)

EditReservationCustomerTab.displayName = 'EditReservationCustomerTab'
